import { useEffect, useState, type ChangeEvent } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Panel, PanelLabel } from "@/components/PageHeader";
import { api, formatApiError } from "@/lib/api";

interface AlertText { label: string; vars: string; title: string; body: string }
interface AlertsConfig { advance_margin_points: number; texts: Record<string, AlertText> }

export function AdminAlertsCard() {
  const [cfg, setCfg] = useState<AlertsConfig | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get<AlertsConfig>("/admin/mentor/alerts").then((r) => setCfg(r.data)).catch(() => undefined); }, []);
  if (!cfg) return null;

  const edit = (key: string, field: "title" | "body", value: string) =>
    setCfg({ ...cfg, texts: { ...cfg.texts, [key]: { ...cfg.texts[key], [field]: value } } });
  const save = async () => {
    setSaving(true);
    try {
      const texts = Object.fromEntries(Object.entries(cfg.texts).map(([k, t]) => [k, { title: t.title, body: t.body }]));
      setCfg((await api.put<AlertsConfig>("/admin/mentor/alerts", { advance_margin_points: cfg.advance_margin_points, texts })).data);
      toast.success("Avisos actualizados");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <Panel testId="admin-alerts-config">
      <PanelLabel>Textos de los avisos</PanelLabel>
      <p className="mt-1 text-xs text-slate-500">Los cambios aplican a los avisos nuevos. Las palabras entre llaves se llenan solas.</p>
      <label className="mt-4 flex items-center gap-3 text-sm text-slate-700">
        Margen de "Va adelantada" (puntos)
        <Input type="number" min={1} max={100} value={cfg.advance_margin_points} className="h-10 w-24"
          onChange={(e: ChangeEvent<HTMLInputElement>) => setCfg({ ...cfg, advance_margin_points: Number(e.target.value) })} data-testid="alerts-margin-input" />
      </label>
      <div className="mt-4 space-y-5">
        {Object.entries(cfg.texts).map(([key, t]) => (
          <div key={key} className="space-y-2 border-t border-[#DDE1E8] pt-4" data-testid={`alert-text-${key}`}>
            <p className="text-sm font-semibold text-[#1B2A4A]">{t.label} <span className="font-mono text-xs font-normal text-slate-400">{t.vars}</span></p>
            <Input value={t.title} maxLength={120} onChange={(e: ChangeEvent<HTMLInputElement>) => edit(key, "title", e.target.value)} className="h-10" data-testid={`alert-title-${key}`} />
            <Textarea value={t.body} maxLength={400} rows={2} onChange={(e: ChangeEvent<HTMLTextAreaElement>) => edit(key, "body", e.target.value)} className="text-sm" data-testid={`alert-body-${key}`} />
          </div>
        ))}
      </div>
      <Button onClick={save} disabled={saving} className="mt-4 bg-[#1B2A4A] text-white hover:bg-[#2A3D63]" data-testid="alerts-save">{saving ? "Guardando…" : "Guardar avisos"}</Button>
    </Panel>
  );
}
